/**
 * @file PlanIntelligencePanel.jsx
 * @module ui/src/components/tenant-admin/PlanIntelligencePanel
 * @description
 *   Plan Intelligence Panel (C-NEW). Replaces the Overview tab content for
 *   approved plans. Renders five metric cards driven by ep 62
 *   GET /api/v1/plans/{id}/intelligence data from usePlanDocument hook.
 *
 *   Five metric cards (in spec order):
 *     1. Assignability    — assignableToday verdict + blocker list
 *     2. Population       — currently assigned / governing today / readiness breakdown
 *     3. Effective State  — effectiveState + effectiveFrom/To
 *     4. Economics        — quota by currency / attainment / payout by currency
 *     5. Health           — five health signals with GREEN/AMBER/RED status
 *
 *   NULL SEMANTICS (non-negotiable per spec §11.1):
 *     - No assignments → counts shown as 0 (not "—")
 *     - No quota rows  → "Not available" (not "$0")
 *     - No calculation → "Not calculated" (never "$0")
 *     - Calculated payout of exactly zero → shown as formatted 0.00
 *
 * @version 1.0.0
 * @since S809
 * @changelog
 *   1.0.0  S809  Initial build — ep62 wired, all five metric cards implemented
 *
 * @consumers PlanDocumentCanvas (activeSection==='overview' on approved plans)
 * @dataSource usePlanDocument hook → document.intelligence (ep 62)
 *
 * @propTypes
 *   intelligence: object|null  — ep 62 response payload (null while loading)
 *   plan: object               — normalized plan from usePlanDocument
 *   onViewPopulation: func     — opens C-54 MatchingPopulationDrawer (ep 30)
 */

import PropTypes from 'prop-types';

// ---------------------------------------------------------------------------
// Style constants
// ---------------------------------------------------------------------------

const font = '"IBM Plex Sans", "Segoe UI", Arial, sans-serif';

const styles = {
  root: {
    fontFamily: font,
    padding: '24px',
    color: 'rgb(var(--color-text))',
    background: 'rgb(var(--color-bg))',
    maxWidth: '960px',
  },
  header: {
    marginBottom: '24px',
  },
  headerTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: 'rgb(var(--color-text))',
    margin: 0,
    marginBottom: '4px',
  },
  headerSub: {
    fontSize: '13px',
    color: 'rgb(var(--color-text-muted))',
    margin: 0,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  card: {
    background: 'rgb(var(--color-surface))',
    borderRadius: '8px',
    border: '1px solid rgba(var(--color-text), 0.08)',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  cardWide: {
    background: 'rgb(var(--color-surface))',
    borderRadius: '8px',
    border: '1px solid rgba(var(--color-text), 0.08)',
    padding: '20px',
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  cardLabel: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgb(var(--color-text-muted))',
    margin: 0,
  },
  cardValue: {
    fontSize: '28px',
    fontWeight: 700,
    color: 'rgb(var(--color-text))',
    margin: 0,
    lineHeight: 1.1,
  },
  cardValueSm: {
    fontSize: '20px',
    fontWeight: 600,
    color: 'rgb(var(--color-text))',
    margin: 0,
  },
  cardMeta: {
    fontSize: '13px',
    color: 'rgb(var(--color-text-muted))',
    margin: 0,
  },
  unavailable: {
    fontSize: '15px',
    fontWeight: 500,
    color: 'rgb(var(--color-text-muted))',
    fontStyle: 'italic',
  },
  badgeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    alignItems: 'center',
  },
  badge: (color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '12px',
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: '99px',
    background: color === 'green'  ? 'rgba(16,185,129,0.12)'
               : color === 'amber' ? 'rgba(245,158,11,0.12)'
               : color === 'red'   ? 'rgba(239,68,68,0.12)'
               : 'rgba(var(--color-text),0.07)',
    color: color === 'green'  ? '#059669'
         : color === 'amber'  ? '#B45309'
         : color === 'red'    ? '#DC2626'
         : 'rgb(var(--color-text-muted))',
  }),
  dot: (color) => ({
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    flexShrink: 0,
    background: color === 'green'  ? '#10B981'
               : color === 'amber' ? '#F59E0B'
               : color === 'red'   ? '#EF4444'
               : 'rgb(var(--color-text-muted))',
  }),
  divider: {
    borderTop: '1px solid rgba(var(--color-text), 0.06)',
    margin: '4px 0',
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 0',
  },
  statLabel: {
    fontSize: '13px',
    color: 'rgb(var(--color-text-muted))',
  },
  statValue: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'rgb(var(--color-text))',
  },
  blockerList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  blockerItem: {
    fontSize: '13px',
    color: '#B45309',
    background: 'rgba(245,158,11,0.07)',
    borderRadius: '6px',
    padding: '8px 10px',
  },
  blockerCode: {
    fontWeight: 600,
    marginBottom: '2px',
    fontSize: '12px',
    letterSpacing: '0.02em',
  },
  blockerAction: {
    color: 'rgb(var(--color-text-muted))',
    fontSize: '12px',
  },
  signalGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '12px',
  },
  signalCard: (color) => ({
    borderRadius: '6px',
    padding: '12px 14px',
    background: color === 'green'  ? 'rgba(16,185,129,0.06)'
               : color === 'amber' ? 'rgba(245,158,11,0.06)'
               : 'rgba(239,68,68,0.06)',
    border: `1px solid ${
      color === 'green'  ? 'rgba(16,185,129,0.18)'
      : color === 'amber' ? 'rgba(245,158,11,0.18)'
      : 'rgba(239,68,68,0.18)'
    }`,
  }),
  signalName: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'rgb(var(--color-text-muted))',
    margin: '0 0 6px 0',
  },
  signalLabel: (color) => ({
    fontSize: '13px',
    fontWeight: 600,
    color: color === 'green'  ? '#059669'
         : color === 'amber'  ? '#B45309'
         : '#DC2626',
  }),
  signalDetail: {
    fontSize: '12px',
    color: 'rgb(var(--color-text-muted))',
    marginTop: '4px',
  },
  loadingCard: {
    background: 'rgb(var(--color-surface))',
    borderRadius: '8px',
    border: '1px solid rgba(var(--color-text), 0.08)',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  skeleton: {
    height: '14px',
    borderRadius: '4px',
    background: 'rgb(var(--color-surface-muted))',
  },
  viewPopBtn: {
    alignSelf: 'flex-start',
    marginTop: '4px',
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function signalColor(status) {
  if (status === 'GREEN') return 'green';
  if (status === 'AMBER') return 'amber';
  if (status === 'RED')   return 'red';
  return 'neutral';
}

function fmtNumber(n) {
  if (n === null || n === undefined) return null;
  return n.toLocaleString();
}

function fmtPct(n) {
  if (n === null || n === undefined) return null;
  return `${(n * 100).toFixed(1)}%`;
}

function fmtCurrency(amount, code) {
  if (amount === null || amount === undefined) return null;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: code, minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${code} ${amount.toFixed(2)}`;
  }
}

function effectiveStateLabel(state) {
  const labels = {
    effective:  'Effective',
    scheduled:  'Scheduled',
    expired:    'Expired',
    pending:    'Pending Approval',
    draft:      'Draft',
    superseded: 'Superseded',
    withdrawn:  'Withdrawn',
  };
  return labels[state] ?? state ?? '—';
}

function effectiveStateColor(state) {
  if (state === 'effective')  return 'green';
  if (state === 'scheduled')  return 'amber';
  if (state === 'expired')    return 'red';
  return 'neutral';
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    // OI-S822-EFFECTIVE-FROM-TZ-DISPLAY fix (S823):
    // Date-only strings (YYYY-MM-DD) parsed by new Date() are treated as UTC midnight,
    // then toLocaleDateString() shifts to local time — showing the previous day for
    // timezones behind UTC (e.g. US zones show Jul 31 when DB has 2026-08-01).
    // Fix: append T00:00:00Z to anchor parsing in UTC, then format with timeZone:'UTC'.
    const normalized = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00Z` : iso;
    return new Date(normalized).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
    });
  } catch { return iso; }
}

function LoadingCards() {
  return (
    <div style={styles.grid}>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} style={styles.loadingCard}>
          <div className="skeleton" style={{ ...styles.skeleton, width: '60%' }} />
          <div className="skeleton" style={{ ...styles.skeleton, width: '40%', height: '28px' }} />
          <div className="skeleton" style={{ ...styles.skeleton, width: '80%' }} />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card 1: Assignability
// ---------------------------------------------------------------------------
function AssignabilityCard({ assignability }) {
  if (!assignability) {
    return (
      <div style={styles.card}>
        <p style={styles.cardLabel}>Assignability</p>
        <p style={styles.unavailable}>Not available</p>
      </div>
    );
  }

  const { assignableToday, reasons } = assignability;
  const color = assignableToday ? 'green' : reasons.length > 0 ? 'red' : 'amber';

  return (
    <div style={styles.card}>
      <p style={styles.cardLabel}>Assignability</p>
      <div style={styles.badgeRow}>
        <span style={styles.badge(color)}>
          <span style={styles.dot(color)} />
          {assignableToday ? 'Assignable today' : 'Not assignable'}
        </span>
      </div>
      {!assignableToday && reasons.length > 0 && (
        <ul style={styles.blockerList}>
          {reasons.map((r) => (
            <li key={r.code} style={styles.blockerItem}>
              <div style={styles.blockerCode}>{r.code.replace(/_/g, ' ')}</div>
              <div style={styles.blockerAction}>{r.recommendedAction}</div>
            </li>
          ))}
        </ul>
      )}
      {assignableToday && (
        <p style={styles.cardMeta}>All assignability conditions met.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card 2: Population
// ---------------------------------------------------------------------------
function PopulationCard({ population, onViewPopulation }) {
  if (!population) {
    return (
      <div style={styles.card}>
        <p style={styles.cardLabel}>Population</p>
        <p style={styles.unavailable}>Not available</p>
      </div>
    );
  }

  const {
    currentlyAssignedCount, governingTodayCount, historicallyAssignedCount,
    eligibleCount, ineligibleCount, holdPendingCount, holdApprovedCount,
    excludedCount, unknownReadinessCount,
  } = population;

  const totalAssigned = currentlyAssignedCount ?? 0;
  const readinessRows = [
    { label: 'Eligible',      value: eligibleCount      ?? 0 },
    { label: 'Ineligible',    value: ineligibleCount    ?? 0 },
    { label: 'Hold — pending', value: holdPendingCount  ?? 0 },
    { label: 'Hold — approved', value: holdApprovedCount ?? 0 },
    { label: 'Excluded',      value: excludedCount      ?? 0 },
    { label: 'Unknown',       value: unknownReadinessCount ?? 0 },
  ].filter((r) => r.value > 0);

  return (
    <div style={styles.card}>
      <p style={styles.cardLabel}>Population</p>
      <div>
        <p style={styles.cardValue}>{fmtNumber(totalAssigned)}</p>
        <p style={styles.cardMeta}>currently assigned positions</p>
      </div>
      <div style={styles.divider} />
      <div style={styles.statRow}>
        <span style={styles.statLabel}>Governing today</span>
        <span style={styles.statValue}>{fmtNumber(governingTodayCount ?? 0)}</span>
      </div>
      <div style={styles.statRow}>
        <span style={styles.statLabel}>Historically assigned</span>
        <span style={styles.statValue}>{fmtNumber(historicallyAssignedCount ?? 0)}</span>
      </div>
      {readinessRows.length > 0 && (
        <>
          <div style={styles.divider} />
          <p style={{ ...styles.cardLabel, marginBottom: '4px' }}>Readiness breakdown</p>
          {readinessRows.map((r) => (
            <div key={r.label} style={styles.statRow}>
              <span style={styles.statLabel}>{r.label}</span>
              <span style={styles.statValue}>{fmtNumber(r.value)}</span>
            </div>
          ))}
        </>
      )}
      {onViewPopulation && (
        <button
          type="button"
          className="text-button"
          style={styles.viewPopBtn}
          onClick={onViewPopulation}
        >
          View full population list →
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card 3: Effective State
// ---------------------------------------------------------------------------
function EffectiveStateCard({ intelligence }) {
  if (!intelligence) {
    return (
      <div style={styles.card}>
        <p style={styles.cardLabel}>Effective State</p>
        <p style={styles.unavailable}>Not available</p>
      </div>
    );
  }

  const { effectiveState, effectiveFrom, effectiveTo, planStatus, version } = intelligence;
  const color = effectiveStateColor(effectiveState);

  return (
    <div style={styles.card}>
      <p style={styles.cardLabel}>Effective State</p>
      <span style={styles.badge(color)}>
        <span style={styles.dot(color)} />
        {effectiveStateLabel(effectiveState)}
      </span>
      <div style={styles.divider} />
      <div style={styles.statRow}>
        <span style={styles.statLabel}>Version</span>
        <span style={styles.statValue}>v{version ?? '—'}</span>
      </div>
      <div style={styles.statRow}>
        <span style={styles.statLabel}>Plan status</span>
        <span style={styles.statValue}>{planStatus ?? '—'}</span>
      </div>
      <div style={styles.statRow}>
        <span style={styles.statLabel}>Effective from</span>
        <span style={styles.statValue}>{formatDate(effectiveFrom) ?? 'Not set'}</span>
      </div>
      <div style={styles.statRow}>
        <span style={styles.statLabel}>Effective to</span>
        <span style={styles.statValue}>{formatDate(effectiveTo) ?? 'Open-ended'}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card 4: Economics
// ---------------------------------------------------------------------------
function EconomicsCard({ economics }) {
  // Null semantics: never show $0 for missing data — show "Not available" / "Not calculated"
  if (!economics) {
    return (
      <div style={styles.card}>
        <p style={styles.cardLabel}>Economics</p>
        <p style={styles.unavailable}>Not available</p>
      </div>
    );
  }

  const { quota, creditTotalReporting, attainmentPct, latestFinalPayout } = economics;

  const hasQuota   = Array.isArray(quota)           && quota.length > 0;
  const hasPayout  = Array.isArray(latestFinalPayout) && latestFinalPayout.length > 0;
  const hasCredit  = creditTotalReporting !== null   && creditTotalReporting !== undefined;
  const hasAttainment = attainmentPct !== null       && attainmentPct !== undefined;

  return (
    <div style={styles.card}>
      <p style={styles.cardLabel}>Economics</p>

      {/* Quota by currency */}
      <div>
        <p style={{ ...styles.statLabel, marginBottom: '6px' }}>Quota (active assignments)</p>
        {hasQuota ? (
          quota.map((q) => (
            <div key={q.quotaReportingCurrencyCode} style={styles.statRow}>
              <span style={styles.statLabel}>{q.quotaReportingCurrencyCode}</span>
              <span style={styles.statValue}>
                {q.effectiveQuota !== null
                  ? fmtCurrency(q.effectiveQuota, q.quotaReportingCurrencyCode)
                  : 'Not available'}
              </span>
            </div>
          ))
        ) : (
          <p style={styles.unavailable}>Not available</p>
        )}
      </div>

      <div style={styles.divider} />

      {/* Attainment */}
      <div style={styles.statRow}>
        <span style={styles.statLabel}>Attainment</span>
        <span style={styles.statValue}>
          {hasAttainment ? fmtPct(attainmentPct) : 'Not calculated'}
        </span>
      </div>

      {/* Credit */}
      <div style={styles.statRow}>
        <span style={styles.statLabel}>Credit (reporting)</span>
        <span style={styles.statValue}>
          {hasCredit ? fmtNumber(Math.round(creditTotalReporting)) : 'Not calculated'}
        </span>
      </div>

      <div style={styles.divider} />

      {/* Payout by currency */}
      <div>
        <p style={{ ...styles.statLabel, marginBottom: '6px' }}>Latest final payout</p>
        {hasPayout ? (
          latestFinalPayout.map((p) => (
            <div key={p.payoutCurrencyCode} style={styles.statRow}>
              <span style={styles.statLabel}>{p.payoutCurrencyCode}</span>
              <span style={styles.statValue}>
                {p.totalPayout !== null
                  ? fmtCurrency(p.totalPayout, p.payoutCurrencyCode)
                  : 'Not calculated'}
              </span>
            </div>
          ))
        ) : (
          <p style={styles.unavailable}>Not calculated</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card 5: Health signals
// ---------------------------------------------------------------------------
const SIGNAL_LABELS = {
  governance:             'Governance',
  assignment:             'Assignment',
  readiness:              'Readiness',
  quota_coverage:         'Quota Coverage',
  calculation_freshness:  'Calculation',
};

function HealthCard({ healthSignals }) {
  if (!healthSignals || healthSignals.length === 0) {
    return (
      <div style={styles.cardWide}>
        <p style={styles.cardLabel}>Health Signals</p>
        <p style={styles.unavailable}>Not available</p>
      </div>
    );
  }

  return (
    <div style={styles.cardWide}>
      <p style={styles.cardLabel}>Health Signals</p>
      <div style={styles.signalGrid}>
        {healthSignals.map((sig) => {
          const color = signalColor(sig.status);
          return (
            <div key={sig.signal} style={styles.signalCard(color)}>
              <p style={styles.signalName}>
                {SIGNAL_LABELS[sig.signal] ?? sig.signal}
              </p>
              <div style={styles.badgeRow}>
                <span style={styles.dot(color)} />
                <span style={styles.signalLabel(color)}>{sig.label}</span>
              </div>
              {sig.detail && (
                <p style={styles.signalDetail}>{sig.detail}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export default function PlanIntelligencePanel({
  intelligence = null,
  plan,
  onViewPopulation = null,
}) {
  // intelligence is null while ep 62 is still loading (non-blocking fetch)
  const isLoading = intelligence === null;

  const planCode = plan?.planCode ?? plan?.plan_code ?? '';
  const planName = plan?.planName ?? plan?.plan_name ?? '';

  return (
    <div
      style={styles.root}
      data-component="PlanIntelligencePanel"
      role="region"
      aria-label="Plan Intelligence"
    >
      <div style={styles.header}>
        <h2 style={styles.headerTitle}>
          Plan Intelligence
          {planCode ? ` — ${planCode}` : ''}
        </h2>
        {planName && (
          <p style={styles.headerSub}>{planName}</p>
        )}
      </div>

      {isLoading ? (
        <LoadingCards />
      ) : (
        <>
          {/* Top row: Assignability + Population + Effective State */}
          <div style={styles.grid}>
            <AssignabilityCard   assignability={intelligence?.assignability} />
            <PopulationCard      population={intelligence?.population}  onViewPopulation={onViewPopulation} />
            <EffectiveStateCard  intelligence={intelligence} />
          </div>

          {/* Second row: Economics */}
          <div style={{ ...styles.grid, marginBottom: '16px' }}>
            <EconomicsCard economics={intelligence?.economics} />
          </div>

          {/* Full-width: Health signals */}
          <div style={{ marginBottom: '8px' }}>
            <HealthCard healthSignals={intelligence?.healthSignals} />
          </div>
        </>
      )}
    </div>
  );
}

PlanIntelligencePanel.propTypes = {
  /** ep 62 intelligence payload — null while loading */
  intelligence:     PropTypes.object,
  /** normalized plan object from usePlanDocument */
  plan:             PropTypes.object.isRequired,
  /** opens MatchingPopulationDrawer (C-54, ep 30) */
  onViewPopulation: PropTypes.func,
};

// Rule 10: defaultProps deprecated in React 18 — defaults set via parameter destructuring above.
